import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import posthog from 'posthog-js';
import { createDebugLogger } from '../utils/debugLogger';
import { apiClient } from '../services/api/apiClient';

const logger = createDebugLogger('[AuthContext]');

// ── AuthUser type ─────────────────────────────────────────────────────────────

export type UserRole = 'USER' | 'MODERATOR' | 'ADMIN';
export type UserStatus = 'ACTIVE' | 'SUSPENDED' | 'BANNED' | 'DEACTIVATED';
export type CreatorTier = 'FREE' | 'PRO' | 'AUTHOR';

export interface ProfileTheme {
  accentColor?: string;
  bgColor?: string;
  textColor?: string;
  headingFont?: string;
  layout?: 'default' | 'editorial' | 'centered' | 'magazine';
}
export type AgeVerificationStatus = 'UNVERIFIED' | 'PENDING' | 'VERIFIED' | 'REJECTED' | 'REVOKED';

export interface AuthUser {
  id: number;
  email: string;
  username: string;
  handle: string;
  displayName: string | null;
  avatar: string | null;
  bio: string | null;
  website: string | null;

  // ── Role & status ────────────────────────────────────────────────────────
  role: UserRole;
  status: UserStatus;

  // ── Creator ──────────────────────────────────────────────────────────────
  isCreator: boolean;
  creatorTier: CreatorTier | null; // null when isCreator is false

  // ── Special flags ─────────────────────────────────────────────────────────
  isBadged: boolean; // Notable creator badge; replaces isVerified
  isSuperviewer: boolean; // Audit role: can view all paywalled content

  // ── Verification ─────────────────────────────────────────────────────────
  emailVerified: boolean;
  ageVerificationStatus: AgeVerificationStatus;

  // ── Onboarding ───────────────────────────────────────────────────────────
  // First-run flag: drives the post-registration welcome flow at /onboarding.
  // Set to true once the user finishes (or skips) the welcome screen, or via
  // the migration backfill for pre-existing accounts.
  hasCompletedOnboarding: boolean;

  // ── Auth method ──────────────────────────────────────────────────────────
  // True when the user has a local password set; false for OAuth-only accounts.
  // Used by the account-deletion flow to decide whether to require password
  // re-auth. Sourced from the backend `/me` response (derived from
  // `user.password !== null` without leaking the hash).
  hasPassword: boolean;

  // ── Creator profile (populated when isCreator is true) ───────────────────
  creatorProfile?: {
    id: number;
    tagline: string | null;
    coverImage: string | null;
    creatorTier: 'FREE' | 'PRO' | 'AUTHOR';
    profileTheme: ProfileTheme | null;
    customDomain: string | null;
    customDomainVerified: boolean;
    subscriptionTiers: Array<{
      id: number;
      name: string;
      price: number;
      sortOrder: number;
      benefits: string[];
    }>;
  } | null;
}

// ── PostHog helpers ───────────────────────────────────────────────────────────

/**
 * Identify the user in PostHog so that all subsequent events are associated
 * with their account. Call this anywhere setUser() is called with a real user.
 */
function identifyUser(user: AuthUser) {
  posthog.identify(String(user.id), {
    email: user.email,
    username: user.username,
    role: user.role,
    creatorTier: user.creatorTier,
    isCreator: user.isCreator,
  });
}

// ── Context ───────────────────────────────────────────────────────────────────

interface AuthContextValue {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (data: {
    email: string;
    password: string;
    username: string;
    displayName?: string;
  }) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  /**
   * On mount: try a silent token refresh using the httpOnly refresh cookie.
   * If it succeeds we get a new access token and can load the user profile.
   * If it fails (no cookie or expired) we just land in logged-out state.
   */
  // Pick up access_token from OAuth callback redirect (?access_token=xxx)
  // or surface an error from a failed OAuth flow (?error=xxx).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const oauthToken = params.get('access_token');
    const oauthError = params.get('error');

    // Clean OAuth params from URL regardless of success/failure
    if (oauthToken || oauthError) {
      const clean = new URL(window.location.href);
      clean.searchParams.delete('access_token');
      clean.searchParams.delete('error');
      window.history.replaceState({}, '', clean.toString());
    }

    if (oauthError) {
      logger.warn('OAuth error:', oauthError);
      setIsLoading(false);
      return;
    }

    if (oauthToken) {
      apiClient.setAccessToken(oauthToken);
      apiClient
        .get<{ success: boolean; data: AuthUser }>('/me')
        .then((res) => {
          if (res?.data) {
            setUser(res.data);
            identifyUser(res.data);
          }
        })
        .catch(() => {
          /* token may be invalid — fall through to logged-out */
        })
        .finally(() => setIsLoading(false));
      return;
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await apiClient.post<{ data: { accessToken: string } }>('/auth/refresh');
        apiClient.setAccessToken(res.data.accessToken);
        const me = await apiClient.get<{ data: AuthUser }>('/me');
        setUser(me.data);
        identifyUser(me.data);
      } catch {
        // No valid session — fine, stay logged out
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const res = await apiClient.post<{ data: { accessToken: string; user: AuthUser } }>(
      '/auth/login',
      { email, password },
    );
    apiClient.setAccessToken(res.data.accessToken);
    setUser(res.data.user);
    identifyUser(res.data.user);
    await apiClient.fetchCSRFToken().catch(() => {});
  }, []);

  const register = useCallback(
    async (data: { email: string; password: string; username: string; displayName?: string }) => {
      await apiClient.post('/auth/register', data);
      await login(data.email, data.password);
    },
    [login],
  );

  const logout = useCallback(async () => {
    try {
      await apiClient.post('/auth/logout');
    } finally {
      apiClient.clearAccessToken();
      setUser(null);
      // Reset PostHog so the next user on this browser gets a fresh anonymous
      // session and is not merged with the user who just logged out.
      posthog.reset();
    }
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const me = await apiClient.get<{ data: AuthUser }>('/me');
      setUser(me.data);
      identifyUser(me.data);
    } catch {
      setUser(null);
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, isLoading, isAuthenticated: !!user, login, register, logout, refreshUser }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
